<template>
  <div>
    <!-- Main Media Manager Dialog -->
    <v-dialog
      v-model="internalValue"
      max-width="1050px"
      scrollable
      persistent
      transition="dialog-bottom-transition"
    >
      <v-card class="rounded-xl overflow-hidden">
        <!-- Header -->
        <v-card-title
          class="d-flex align-center justify-space-between pa-4 grey lighten-4"
        >
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2"
              >mdi-image-multiple-outline</v-icon
            >
            <span class="text-h6 font-weight-bold">Media Manager</span>
          </div>
          <v-btn icon @click="closeDialog" :disabled="isProcessing">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <!-- Main Body -->
        <v-card-text class="pa-4 pa-sm-6" style="max-height: 85vh">
          <!-- Hidden File Input -->
          <input
            type="file"
            ref="fileInput"
            style="display: none"
            accept="image/*"
            @change="onFileSelected"
          />

          <!-- Folder Selection & Upload Trigger Bar -->
          <v-row class="mb-4" align="center">
            <v-col cols="12" sm="7" md="8">
              <v-select
                v-model="selectedFolder"
                :items="folders"
                label="Select Image Folder"
                prepend-inner-icon="mdi-folder-outline"
                outlined
                dense
                hide-details
                rounded
                :disabled="isProcessing"
                @change="getImages"
              ></v-select>
            </v-col>

            <v-col cols="12" sm="5" md="4" class="text-sm-right">
              <v-tooltip bottom :disabled="!isUploadDisabled">
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on" class="d-inline-block w-full">
                    <v-btn
                      color="primary"
                      rounded
                      block
                      depressed
                      :disabled="
                        !selectedFolder || isUploadDisabled || isProcessing
                      "
                      :loading="isProcessing && !imageToBeReplaced"
                      @click="triggerNewUpload"
                    >
                      <v-icon left>mdi-plus-circle-outline</v-icon>
                      Add New
                    </v-btn>
                  </div>
                </template>
                <span>You can Delete or replace the existing one.</span>
              </v-tooltip>
            </v-col>
          </v-row>

          <!-- Logo Folder Warning Banner -->
          <v-alert
            v-if="selectedFolder === 'logos' && images.length >= 1"
            type="info"
            text
            dense
            rounded="lg"
            class="mb-4"
            icon="mdi-information-outline"
          >
            You Can Use <strong>Replace</strong> or <strong>Delete</strong> on
            the existing image below.
          </v-alert>

          <!-- Content States (Loading / Empty / Grid) -->
          <div
            v-if="loadingImages"
            class="d-flex justify-center align-center py-12"
          >
            <v-progress-circular
              indeterminate
              color="primary"
              size="48"
            ></v-progress-circular>
          </div>

          <div
            v-else-if="!selectedFolder"
            class="text-center py-12 grey--text text--darken-1"
          >
            <v-icon size="64" color="grey lighten-2"
              >mdi-folder-search-outline</v-icon
            >
            <p class="text-subtitle-1 mt-2">Select a folder to view images.</p>
          </div>

          <div
            v-else-if="images.length === 0"
            class="text-center py-12 grey--text text--darken-1"
          >
            <v-icon size="64" color="grey lighten-2"
              >mdi-image-off-outline</v-icon
            >
            <p class="text-subtitle-1 mt-2 mb-0">This folder is empty.</p>
            <p class="text-caption">Click "Add New" to upload an image.</p>
          </div>

          <!-- Image Gallery Grid -->
          <v-row v-else>
            <v-col
              v-for="(image, index) in images"
              :key="`${image}-${index}`"
              cols="12"
              sm="6"
              md="4"
            >
              <v-hover v-slot="{ hover }">
                <v-card
                  outlined
                  class="rounded-xl overflow-hidden transition-swing"
                  :elevation="hover ? 6 : 1"
                >
                  <v-img
                    :src="getImageUrl(image)"
                    height="200px"
                    contain
                    class="grey lighten-4 d-flex align-end"
                  >
                    <template v-slot:placeholder>
                      <v-row
                        class="fill-height ma-0"
                        align="center"
                        justify="center"
                      >
                        <v-progress-circular
                          indeterminate
                          color="grey lighten-2"
                        ></v-progress-circular>
                      </v-row>
                    </template>
                  </v-img>

                  <v-card-actions class="justify-space-between pa-2 bg-white">
                    <v-btn
                      text
                      small
                      rounded
                      color="primary"
                      :disabled="isProcessing"
                      @click="triggerReplace(image)"
                    >
                      <v-icon left small>mdi-swap-horizontal</v-icon>
                      Replace
                    </v-btn>
                    <v-btn
                      text
                      small
                      rounded
                      color="error"
                      :disabled="isProcessing"
                      @click="deleteImg(image)"
                    >
                      <v-icon left small>mdi-delete-outline</v-icon>
                      Delete
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-hover>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <!-- Footer Actions -->
        <v-card-actions class="pa-4 grey lighten-5 justify-end">
          <v-btn text rounded @click="closeDialog" :disabled="isProcessing"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Image Cropper Dialog -->
    <v-dialog v-model="cropperDialog" max-width="750px" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="pa-4 grey lighten-4">
          <v-icon left color="primary">mdi-crop</v-icon>
          <span class="text-h6 font-weight-bold">Crop & Edit Image</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="cancelCropping"
            :disabled="isRemovingBg || isProcessing"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-4">
          <!-- Cropper Container -->
          <div
            class="cropper-container rounded-lg overflow-hidden grey lighten-3 position-relative"
          >
            <!-- Background Removal Overlay Loading -->
            <v-overlay
              absolute
              :value="isRemovingBg"
              opacity="0.75"
              color="white"
              class="text-center"
            >
              <v-progress-circular
                indeterminate
                color="deep-purple"
                size="50"
                class="mb-3"
              ></v-progress-circular>
              <div class="text-body-2 font-weight-medium deep-purple--text">
                Removing background with AI...
              </div>
            </v-overlay>

            <vue-cropper
              ref="cropper"
              :src="rawImageSrc"
              alt="Source Image"
              :aspect-ratio="
                selectedFolder === 'logos' ? 1 : currentAspectRatio
              "
              :view-mode="1"
              drag-mode="move"
              :auto-crop-area="0.8"
              :background="true"
              :rotatable="true"
              :class="[
                'v-cropper-custom',
                { 'round-cropper': cropShape === 'round' },
              ]"
            />
          </div>

          <!-- Toolbar Controls -->
          <v-sheet
            class="d-flex align-center justify-center flex-wrap mt-4 pa-2 rounded-lg grey lighten-4"
            outlined
          >
            <!-- Rotate Buttons -->
            <v-btn
              icon
              @click="rotate(-90)"
              class="mx-1"
              :disabled="isRemovingBg"
            >
              <v-icon>mdi-rotate-left</v-icon>
            </v-btn>
            <v-btn
              icon
              @click="rotate(90)"
              class="mx-1"
              :disabled="isRemovingBg"
            >
              <v-icon>mdi-rotate-right</v-icon>
            </v-btn>

            <v-divider
              vertical
              class="mx-2 hidden-xs-only"
              style="height: 24px"
            ></v-divider>

            <!-- Aspect Ratio Selector -->
            <v-btn-toggle
              v-model="currentAspectRatio"
              mandatory
              dense
              rounded
              class="mx-1 my-1"
              :disabled="cropShape === 'round' || isRemovingBg"
            >
              <v-btn :value="NaN" text small>Free</v-btn>
              <v-btn :value="1" text small>1:1</v-btn>
              <v-btn :value="16 / 9" text small>16:9</v-btn>
            </v-btn-toggle>

            <v-divider
              vertical
              class="mx-2 hidden-xs-only"
              style="height: 24px"
            ></v-divider>

            <!-- Feature 1: Crop Shape Selection -->
            <v-btn-toggle
              v-model="cropShape"
              mandatory
              dense
              rounded
              class="mx-1 my-1"
              :disabled="isRemovingBg"
            >
              <v-btn value="square" text small>
                <v-icon left small>mdi-crop-square</v-icon>
                Square
              </v-btn>
              <v-btn value="round" text small>
                <v-icon left small>mdi-circle-outline</v-icon>
                Round
              </v-btn>
            </v-btn-toggle>

            <v-divider
              vertical
              class="mx-2 hidden-xs-only"
              style="height: 24px"
            ></v-divider>

            <!-- Feature 2: Remove Background AI Button -->
            <v-btn
              color="deep-purple"
              dark
              small
              rounded
              depressed
              class="mx-1 my-1"
              :loading="isRemovingBg"
              :disabled="isProcessing || isRemovingBg"
              @click="removeBg"
            >
              <v-icon left small>mdi-auto-fix</v-icon>
              Remove BG
            </v-btn>
          </v-sheet>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4 grey lighten-5">
          <v-btn
            text
            rounded
            @click="cancelCropping"
            :disabled="isRemovingBg || isProcessing"
            >Cancel</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            rounded
            depressed
            :loading="isProcessing"
            :disabled="isRemovingBg"
            @click="processCroppedImage"
          >
            <v-icon left>mdi-check</v-icon>
            Save &amp; Upload
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "@/plugins/axios";
import companiesJs from "@/plugins/companies";
import VueCropper from "vue-cropperjs";
import removeBackground from "@imgly/background-removal";

export default {
  name: "MediaManagerDialogWithCrop",
  components: { VueCropper },
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    folders: {
      type: Array,
      default: () => ["logos", "stamps"],
    },
  },
  data() {
    return {
      selectedFolder: "logos",
      images: [],
      imageToBeReplaced: null,
      loadingImages: false,
      isProcessing: false,

      // Cropper state
      cropperDialog: false,
      rawImageSrc: null,
      currentFileName: null,
      currentAspectRatio: NaN,

      // Feature States
      cropShape: "square", // 'square' | 'round'
      isRemovingBg: false,
      isBgRemoved: false,
      company: null,
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.dialog;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    getAccessToken() {
      return this.$store.getters.getAccessToken;
    },

    getApiUrl() {
      return this.$store.getters.getApiUrl;
    },

    isUploadDisabled() {
      return this.selectedFolder === "logos" && this.images.length >= 1;
    },
    getUserCompany() {
      return this.$store.getters.getUserCompany;
    },
  },
  watch: {
    dialog(val) {
      if (val && this.folders.length > 0 && !this.selectedFolder) {
        this.selectedFolder = this.folders[0];
        this.getImages();
      }
    },
    currentAspectRatio(newRatio) {
      if (!this.$refs.cropper) return;
      if (this.selectedFolder === "logos" || this.cropShape === "round") {
        this.$refs.cropper.setAspectRatio(1);
      } else {
        this.$refs.cropper.setAspectRatio(newRatio);
      }
    },
    cropShape(newShape) {
      if (newShape === "round") {
        this.currentAspectRatio = 1;
        if (this.$refs.cropper) {
          this.$refs.cropper.setAspectRatio(1);
        }
      }
    },
  },
  async created() {
    const companyData = await companiesJs.getUserCompany();
    this.company = companyData?.company;
  },
  async mounted() {
    await this.getImages();
  },
  methods: {
    closeDialog() {
      if (this.isProcessing) return;
      this.internalValue = false;
    },

    getImageUrl(image) {
      return `${this.getApiUrl}/images/${this.selectedFolder}/${this.company?.emis}/${image}`;
    },

    triggerNewUpload() {
      if (this.isUploadDisabled) {
        this.$toast.info(
          "Logo folder is limited to one image. Please delete the existing logo first."
        );
        return;
      }
      this.imageToBeReplaced = null;
      this.$refs.fileInput.click();
    },

    triggerReplace(image) {
      this.imageToBeReplaced = image;
      this.$refs.fileInput.click();
    },

    onFileSelected(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        this.$toast.error("Please select a valid image file.");
        event.target.value = "";
        return;
      }

      this.currentFileName = file.name;
      this.cropShape = "square";
      this.isBgRemoved = false;

      const fileReader = new FileReader();
      fileReader.addEventListener("load", () => {
        this.rawImageSrc = fileReader.result;
        this.cropperDialog = true;

        this.$nextTick(() => {
          if (this.selectedFolder === "logos") {
            this.currentAspectRatio = 1;
          }
        });

        event.target.value = "";
      });

      fileReader.readAsDataURL(file);
    },

    rotate(deg) {
      if (this.$refs.cropper) {
        this.$refs.cropper.rotate(deg);
      }
    },

    cancelCropping() {
      this.cropperDialog = false;
      this.rawImageSrc = null;
      this.imageToBeReplaced = null;
      this.cropShape = "square";
      this.isBgRemoved = false;
    },

    // --- Feature 2: Background Removal ---
    async removeBg() {
      if (!this.rawImageSrc) return;
      this.isRemovingBg = true;

      try {
        // Run AI background removal in-browser
        const blob = await removeBackground(this.rawImageSrc);

        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          const resultBase64 = reader.result;
          this.rawImageSrc = resultBase64;

          if (this.$refs.cropper) {
            this.$refs.cropper.replace(resultBase64);
          }

          this.isBgRemoved = true;
          this.$toast.success("Background removed successfully!");
          this.isRemovingBg = false;
        };
      } catch (error) {
        console.error("Background removal error:", error);
        this.$toast.error("Failed to remove background.");
        this.isRemovingBg = false;
      }
    },

    // --- Processing & Round Mask ---
    processCroppedImage() {
      const cropper = this.$refs.cropper;
      if (!cropper) return;

      let canvas = cropper.getCroppedCanvas();
      if (!canvas) {
        this.$toast.error("Could not generate cropped image.");
        return;
      }

      // Feature 1: Round Crop Clipping logic
      if (this.cropShape === "round") {
        const roundCanvas = document.createElement("canvas");
        const ctx = roundCanvas.getContext("2d");
        const size = Math.min(canvas.width, canvas.height);

        roundCanvas.width = size;
        roundCanvas.height = size;

        // Draw circular mask clip
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();

        ctx.drawImage(
          canvas,
          (canvas.width - size) / 2,
          (canvas.height - size) / 2,
          size,
          size,
          0,
          0,
          size,
          size
        );

        canvas = roundCanvas;
      }

      // Ensure PNG format if round cropped or background removed for transparency
      const requiresPng = this.cropShape === "round" || this.isBgRemoved;
      const mimeType = requiresPng
        ? "image/png"
        : this.currentFileName.endsWith(".png")
        ? "image/png"
        : "image/jpeg";
      const croppedBase64 = canvas.toDataURL(mimeType);

      // Fix file extension if forced to PNG
      let uploadFileName = this.currentFileName;
      if (requiresPng && !uploadFileName.toLowerCase().endsWith(".png")) {
        uploadFileName = uploadFileName.replace(/\.[^/.]+$/, "") + ".png";
      }

      if (this.imageToBeReplaced) {
        this.executeReplace(croppedBase64, uploadFileName);
      } else {
        this.executeUpload(croppedBase64, uploadFileName);
      }
    },

    async getImages() {
      if (!this.selectedFolder) return;

      this.loadingImages = true;
      try {
        const response = await axios.postDataWithToken(
          "/auth/get-images-for-given-folder",
          { folder: this.selectedFolder },
          this.getAccessToken
        );
        this.images = Array.isArray(response) ? response : [];
      } catch (error) {
        console.error(error);
        this.$toast.error("Failed to load images.");
      } finally {
        this.loadingImages = false;
      }
    },

    async executeUpload(base64Image, fileName) {
      this.isProcessing = true;
      try {
        const response = await axios.postDataWithToken(
          "/auth/upload-image",
          {
            image64: base64Image,
            name: fileName,
            folder: this.selectedFolder,
          },
          this.getAccessToken
        );
        this.images = response;
        this.$toast.success("Image uploaded successfully.");
        this.cropperDialog = false;
      } catch (error) {
        this.$toast.error("Upload failed.");
      } finally {
        this.isProcessing = false;
      }
    },

    async executeReplace(base64Image, fileName) {
      this.isProcessing = true;
      try {
        const response = await axios.postDataWithToken(
          "/auth/replace-image",
          {
            image64: base64Image,
            folder: this.selectedFolder,
            oldName: this.imageToBeReplaced,
            newName: fileName,
          },
          this.getAccessToken
        );
        this.images = response;
        this.$toast.success("Image replaced successfully.");
        this.cropperDialog = false;
      } catch (error) {
        this.$toast.error("Replacement failed.");
      } finally {
        this.imageToBeReplaced = null;
        this.isProcessing = false;
      }
    },

    async deleteImg(image) {
      this.isProcessing = true;
      try {
        const response = await axios.postDataWithToken(
          "/auth/delete-image",
          { image: image, folder: this.selectedFolder },
          this.getAccessToken
        );
        this.images = response;
        this.$toast.success("Image deleted.");
      } catch (error) {
        this.$toast.error("Delete failed.");
      } finally {
        this.isProcessing = false;
      }
    },
  },
};
</script>

<style scoped>
/* Circular mask overlay for cropper */
::v-deep .round-cropper .cropper-view-box,
::v-deep .round-cropper .cropper-face {
  border-radius: 50% !important;
}

.position-relative {
  position: relative;
}

.w-full {
  width: 100%;
}

/* Ensure cropper container has a definite height */
.cropper-container {
  max-height: 400px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* vue-cropperjs specific styling to fit constraints */
.v-cropper-custom {
  max-width: 100%;
  max-height: 400px;
}
</style>
